import {
  getAdminSessions,
  getChatHistory,
  createChatSession,
  sendMessage,
  closeChatSession,
  assignAgent,
  getSupportAgents,
  sendEmailFallback,
  updateAgentStatus,
  getSupportStats,
} from "./asyncThunks";

export const getAdminSessionsBuilder = (builder) => {
  builder.addCase(getAdminSessions.pending, (state) => {
    state.adminSessions.loading = true;
    state.adminSessions.error = null;
  });
  builder.addCase(getAdminSessions.fulfilled, (state, { payload }) => {
    state.adminSessions.loading = false;

    // Preserve existing messages when refreshing sessions
    const newSessions = payload.data || [];
    const existingSessions = state.adminSessions.data;

    // Merge new session data with existing messages
    const mergedSessions = newSessions.map((newSession) => {
      const existingSession = existingSessions.find(
        (existing) => existing.id === newSession.id
      );
      if (
        existingSession &&
        existingSession.messages &&
        existingSession.messages.length > 0
      ) {
        // Keep existing messages if they exist
        return {
          ...newSession,
          messages: existingSession.messages,
        };
      }
      return newSession;
    });

    state.adminSessions.data = mergedSessions;
    state.adminSessions.error = null;
  });
  builder.addCase(getAdminSessions.rejected, (state, { error }) => {
    state.adminSessions.loading = false;
    state.adminSessions.error = error.message;
  });
};

export const getChatHistoryBuilder = (builder) => {
  builder.addCase(getChatHistory.pending, (state) => {
    state.chatHistory.loading = true;
    state.chatHistory.error = null;
  });
  builder.addCase(getChatHistory.fulfilled, (state, { payload, meta }) => {
    state.chatHistory.loading = false;
    state.chatHistory.data = payload.data || [];
    state.chatHistory.error = null;

    // Also update the session's messages in adminSessions
    const chatId = meta.arg; // The chatId passed to getChatHistory
    const session = state.adminSessions.data.find(
      (session) => session.id === chatId
    );
    if (session && payload.data) {
      // Debug log to see what backend returns
      console.log("Backend returned messages:", payload.data);

      // Transform messages to match the expected format
      const newMessages = payload.data.map((msg) => {
        console.log("🟡 [BUILDER] Processing message:", {
          id: msg.id,
          senderType: msg.senderType,
          message: msg.message,
          createdAt: msg.createdAt,
          timestamp: msg.timestamp,
        });

        const transformedMessage = {
          id: msg.id,
          text: msg.message, // Ensure we use 'message' field from backend
          sender: msg.senderType === "admin" ? "admin" : "user",
          timestamp: msg.createdAt || msg.timestamp,
        };

        console.log("🟡 [BUILDER] Transformed to:", transformedMessage);
        return transformedMessage;
      });

      console.log(
        "🟡 [BUILDER] Existing session messages before merge:",
        session.messages?.map((m) => ({
          id: m.id,
          sender: m.sender,
          text: m.text,
          isTemp: m.isTemp,
        }))
      );
      console.log(
        "🟡 [BUILDER] New messages from backend:",
        newMessages.map((m) => ({ id: m.id, sender: m.sender, text: m.text }))
      );

      if (session.messages && session.messages.length > 0) {
        const existingMessages = session.messages;
        const mergedMessages = [];

        // First, add all backend messages (authoritative)
        newMessages.forEach((newMsg) => {
          mergedMessages.push(newMsg);
        });

        // Then, add any WebSocket messages that aren't in backend yet
        existingMessages.forEach((existing) => {
          // Skip if it's a backend message we already added
          const alreadyAdded = newMessages.find(
            (newMsg) =>
              newMsg.id === existing.id ||
              (newMsg.text === existing.text &&
                newMsg.sender === existing.sender)
          );

          if (!alreadyAdded) {
            // This is likely a fresh WebSocket message not yet in backend
            console.log("🟡 [BUILDER] Preserving WebSocket message:", existing);
            mergedMessages.push(existing);
          }
        });

        // Sort by timestamp
        session.messages = mergedMessages.sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
        );
      } else {
        session.messages = newMessages;
      }

      console.log(
        "🟡 [BUILDER] Final merged messages:",
        session.messages.map((m) => ({
          id: m.id,
          sender: m.sender,
          text: m.text,
        }))
      );
    }
  });
  builder.addCase(getChatHistory.rejected, (state, { error }) => {
    state.chatHistory.loading = false;
    state.chatHistory.error = error.message;
  });
};

export const createChatSessionBuilder = (builder) => {
  builder.addCase(createChatSession.pending, (state) => {
    state.createSession.loading = true;
    state.createSession.error = null;
  });
  builder.addCase(createChatSession.fulfilled, (state, { payload }) => {
    state.createSession.loading = false;
    state.createSession.data = payload.data || null;
    state.createSession.error = null;
  });
  builder.addCase(createChatSession.rejected, (state, { error }) => {
    state.createSession.loading = false;
    state.createSession.error = error.message;
  });
};

export const sendMessageBuilder = (builder) => {
  builder.addCase(sendMessage.pending, (state) => {
    state.sendMessage.loading = true;
    state.sendMessage.error = null;
  });
  builder.addCase(sendMessage.fulfilled, (state, { payload }) => {
    state.sendMessage.loading = false;
    state.sendMessage.data = payload.data || null;
    state.sendMessage.error = null;
  });
  builder.addCase(sendMessage.rejected, (state, { error }) => {
    state.sendMessage.loading = false;
    state.sendMessage.error = error.message;
  });
};

export const closeChatSessionBuilder = (builder) => {
  builder.addCase(closeChatSession.pending, (state) => {
    state.closeSession.loading = true;
    state.closeSession.error = null;
  });
  builder.addCase(closeChatSession.fulfilled, (state, { payload, meta }) => {
    state.closeSession.loading = false;
    state.closeSession.data = payload.data || null;
    state.closeSession.error = null;

    // Remove closed session from admin sessions list
    const chatId = meta.arg; // The chatId passed to closeChatSession
    state.adminSessions.data = state.adminSessions.data.filter(
      (session) => session.id !== chatId
    );
  });
  builder.addCase(closeChatSession.rejected, (state, { error }) => {
    state.closeSession.loading = false;
    state.closeSession.error = error.message;
  });
};

export const assignAgentBuilder = (builder) => {
  builder.addCase(assignAgent.pending, (state) => {
    state.assignAgent.loading = true;
    state.assignAgent.error = null;
  });
  builder.addCase(assignAgent.fulfilled, (state, { payload }) => {
    state.assignAgent.loading = false;
    state.assignAgent.data = payload.data || null;
    state.assignAgent.error = null;
  });
  builder.addCase(assignAgent.rejected, (state, { error }) => {
    state.assignAgent.loading = false;
    state.assignAgent.error = error.message;
  });
};

export const getSupportAgentsBuilder = (builder) => {
  builder.addCase(getSupportAgents.pending, (state) => {
    state.agents.loading = true;
    state.agents.error = null;
  });
  builder.addCase(getSupportAgents.fulfilled, (state, { payload }) => {
    state.agents.loading = false;
    state.agents.data = payload.data || [];
    state.agents.error = null;
  });
  builder.addCase(getSupportAgents.rejected, (state, { error }) => {
    state.agents.loading = false;
    state.agents.error = error.message;
  });
};

export const sendEmailFallbackBuilder = (builder) => {
  builder.addCase(sendEmailFallback.pending, (state) => {
    state.emailFallback.loading = true;
    state.emailFallback.error = null;
  });
  builder.addCase(sendEmailFallback.fulfilled, (state, { payload }) => {
    state.emailFallback.loading = false;
    state.emailFallback.data = payload.data || null;
    state.emailFallback.error = null;
  });
  builder.addCase(sendEmailFallback.rejected, (state, { error }) => {
    state.emailFallback.loading = false;
    state.emailFallback.error = error.message;
  });
};

export const updateAgentStatusBuilder = (builder) => {
  builder.addCase(updateAgentStatus.pending, (state) => {
    state.agentStatus.loading = true;
    state.agentStatus.error = null;
  });
  builder.addCase(updateAgentStatus.fulfilled, (state, { payload }) => {
    state.agentStatus.loading = false;
    state.agentStatus.data = payload.data || null;
    state.agentStatus.error = null;
  });
  builder.addCase(updateAgentStatus.rejected, (state, { error }) => {
    state.agentStatus.loading = false;
    state.agentStatus.error = error.message;
  });
};

export const getSupportStatsBuilder = (builder) => {
  builder.addCase(getSupportStats.pending, (state) => {
    state.stats.loading = true;
    state.stats.error = null;
  });
  builder.addCase(getSupportStats.fulfilled, (state, { payload }) => {
    state.stats.loading = false;
    state.stats.data = payload.data || {};
    state.stats.error = null;
  });
  builder.addCase(getSupportStats.rejected, (state, { error }) => {
    state.stats.loading = false;
    state.stats.error = error.message;
  });
};
